import { Component, OnInit, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';

// Services
import { ProgramService } from '../../services/program/program.service';
import { ProgramsApiService } from '../../services/programs/programs-api.service';
import { AuthenticationCheckService } from '../../services/auth/authentication-check.service';
import { isFulfillmentAdminUser } from '../../services/auth/portal-role';
import { AccountManagerService } from '../../superadmin/features/account-manager/services/account-manager.service';

// Shared UI Components
import {
  ButtonComponent,
  IconComponent,
  IconBadgeComponent,
  PillComponent,
  StatsCardComponent,
  SectionHeaderComponent,
  TabBarComponent,
  LoadingSpinnerComponent,
  InputComponent,
  SelectComponent,
  type TabItem,
  type SectionAction,
  type SelectOption,
} from '@packages/ng-shared-ui';

interface Project {
  id: number;
  uid: string; // Program UID for X-Program-Id header
  name: string;
  startDate: string;
  endDate: string;
  accountManager: string;
  managerInitials: string;
  managerEmail?: string; // Account manager email from API
  managerName?: string; // Account manager name from API
  status: 'ongoing' | 'completed' | 'on-hold';
  // Additional fields for form prefilling
  programCode: string;
  applicationName: string | null;
  mappingType: number | null;
  hubAdminUrl: string | null;
  salesType: number | null;
  createTemplate: number | null;
}

type TabType = 'ongoing' | 'completed' | 'on-hold';

@Component({
  selector: 'app-programs-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    IconComponent,
    IconBadgeComponent,
    PillComponent,
    StatsCardComponent,
    SectionHeaderComponent,
    TabBarComponent,
    LoadingSpinnerComponent,
    InputComponent,
    SelectComponent,
  ],
  templateUrl: './programs-list.component.html',
  styleUrls: ['./programs-list.component.scss'],
})
export class ProgramsListComponent implements OnInit {
  private router = inject(Router);
  private programService = inject(ProgramService);
  private programsApi = inject(ProgramsApiService);
  private accountManagerService = inject(AccountManagerService);
  private auth = inject(AuthenticationCheckService);
  private destroyRef = inject(DestroyRef);

  // Loading state
  isLoading = signal(false);
  error = signal<string | null>(null);
  updatingProgramId = signal<number | null>(null); // Track which program status is being updated

  // Search and filter form fields
  searchQuery = '';
  accountManagerFilter = '';

  accountManagerOptions = signal<SelectOption[]>([
    { label: 'All Account Managers', value: '' },
  ]);

  // Active tab
  activeTab = signal<TabType>('ongoing');

  // Projects data from API
  projects = signal<Project[]>([]);

  // Counts from API response
  ongoingCount = signal(0);
  completedCount = signal(0);
  onHoldCount = signal(0);

  // Pagination
  currentPage = signal(0);
  pageSize = signal(20);
  totalRecords = signal(0);
  pageCount = signal(0);

  getPageNumbers = computed(() => {
    const total = this.pageCount();
    if (total <= 0) {
      return [];
    }
    const current = this.currentPage();
    const pages: number[] = [];

    if (total <= 7) {
      for (let i = 0; i < total; i++) {
        pages.push(i);
      }
    } else if (current <= 3) {
      pages.push(0, 1, 2, 3, 4, -1, total - 1);
    } else if (current >= total - 4) {
      pages.push(0, -1, total - 5, total - 4, total - 3, total - 2, total - 1);
    } else {
      pages.push(0, -1, current - 1, current, current + 1, -1, total - 1);
    }

    return pages;
  });

  Math = Math;

  // Tabs configuration for TabBar component
  tabs = computed<TabItem[]>(() => [
    { id: 'ongoing', label: 'Ongoing', icon: 'control-play', count: this.ongoingCount() },
    { id: 'completed', label: 'Completed', icon: 'check', count: this.completedCount() },
    { id: 'on-hold', label: 'On Hold', icon: 'control-pause', count: this.onHoldCount() },
  ]);

  // Section header actions
  overviewAction: SectionAction = {
    label: 'Create New Program',
    icon: 'ti-plus',
    variant: 'gradient',
    action: () => this.createNewProgram(),
  };

  managementAction: SectionAction = {
    label: 'New Program',
    icon: 'ti-plus',
    variant: 'outline-primary',
    action: () => this.createNewProgram(),
  };

  // Role-based getters
  get isSuperAdmin(): boolean {
    const userRole = localStorage.getItem('user_role');
    return userRole === '2';
  }

  get isFulfillmentAdmin(): boolean {
    return isFulfillmentAdminUser();
  }

  get userRole(): number {
    return Number(localStorage.getItem('user_role')) || 0;
  }

  // Filtered projects based on active tab (search/manager filters handled by API)
  filteredProjects = computed(() => {
    const tab = this.activeTab();
    return this.projects().filter(p => p.status === tab);
  });

  ngOnInit(): void {
    if (this.isFulfillmentAdmin) {
      this.router.navigate(['/fulfillmentadmin/vendor-list']);
      return;
    }

    // Clear any existing program context to ensure master token is used
    // This handles both "Change Program" and back button navigation
    this.programService.clearProgram();

    // Restore master token if returning from program-members page
    const masterToken = localStorage.getItem('master_token');
    if (masterToken) {
      this.auth.saveAuth(masterToken);
      localStorage.removeItem('master_token'); // Clean up after restoring
    }

    this.loadAccountManagers();
    this.loadPrograms();
  }

  private loadAccountManagers(): void {
    this.accountManagerService
      .getAccountManagers(3)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: managers => {
          this.accountManagerOptions.set([
            { label: 'All Account Managers', value: '' },
            ...managers.map(manager => ({
              label: manager.username,
              value: manager.email,
            })),
          ]);
        },
        error: err => {
          console.error('Failed to load account managers:', err);
        },
      });
  }

  /**
   * Load programs from API
   */
  loadPrograms(): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.programsApi
      .getPrograms({
        page: this.currentPage() + 1,
        search: this.searchQuery.trim(),
        manager_name: '',
        manager_email: this.accountManagerFilter,
      })
      .subscribe({
      next: response => {
        // Set counts from API response
        this.ongoingCount.set(response.ongoing_count || 0);
        this.completedCount.set(response.completed_count || 0);
        this.onHoldCount.set(response.hold_count || 0);

        if (response.pagination) {
          this.totalRecords.set(response.pagination.total_count);
          this.pageSize.set(response.pagination.per_page);
          this.pageCount.set(response.pagination.page_count);
          this.currentPage.set(Math.max(0, response.pagination.current_page - 1));
        } else {
          this.totalRecords.set(response.total || response.programs?.length || 0);
          this.pageCount.set(1);
        }

        // Map API response to Project interface
        const mappedPrograms: Project[] = (response.programs || []).map(
          (item): Project => ({
            id: item.id,
            uid: item.uid,
            name: item.program_name || 'Unnamed Program',
            startDate: item.prog_start_date || '', // Keep in YYYY-MM-DD format for form inputs
            endDate: item.prog_end_date || '', // Keep in YYYY-MM-DD format for form inputs
            accountManager: item.manager_name || 'Not Assigned',
            managerInitials: this.getInitials(item.manager_name),
            managerEmail: item.manager_email,
            managerName: item.manager_name,
            status: this.mapStatusLabel(item.status_label),
            programCode: item.program_code || '',
            applicationName: item.application_name ?? null,
            mappingType: typeof item.mapping_type === 'number' ? item.mapping_type : null,
            hubAdminUrl: item.panel_url ?? null,
            salesType: typeof item.sales_type === 'number' ? item.sales_type : null,
            createTemplate:
              typeof item.create_template === 'number' ? item.create_template : null,
          })
        );
        this.projects.set(mappedPrograms);
        this.isLoading.set(false);
      },
      error: err => {
        console.error('Failed to load programs:', err);
        this.error.set(err.error?.message || 'Failed to load programs');
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Format date from YYYY-MM-DD to display format
   */
  formatDateForDisplay(dateStr: string): string {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  /**
   * Map API status_label to component status type
   */
  private mapStatusLabel(statusLabel: string): 'ongoing' | 'completed' | 'on-hold' {
    const label = (statusLabel || '').toLowerCase();
    if (label === 'completed') return 'completed';
    if (label === 'on hold' || label === 'on-hold' || label === 'hold') return 'on-hold';
    return 'ongoing'; // Default to ongoing
  }

  /**
   * Map API status number to component status type (fallback)
   * status: 1 = ongoing, 2 = completed, 3 = on-hold
   */
  private mapStatus(status: number): 'ongoing' | 'completed' | 'on-hold' {
    if (status === 2) return 'completed';
    if (status === 3) return 'on-hold';
    return 'ongoing'; // status 1 or default
  }

  /**
   * Get initials from name
   */
  private getInitials(name: string): string {
    if (!name) return 'NA';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  applyFilters(): void {
    this.currentPage.set(0);
    this.loadPrograms();
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.accountManagerFilter = '';
    this.currentPage.set(0);
    this.loadPrograms();
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.pageCount()) {
      this.currentPage.set(page);
      this.loadPrograms();
    }
  }

  setActiveTab(tab: TabType): void {
    this.activeTab.set(tab);
  }

  onTabChange(tabId: string): void {
    this.activeTab.set(tabId as TabType);
  }

  createNewProgram(): void {
    // Set program ID to 0 for new program creation
    this.programService.setNewProgram();
    this.router.navigate(['/superAdmin/about-program']);
  }

  viewProject(project: Project): void {
    // First, call program-login to get program-specific token
    this.isLoading.set(true);

    // Get email from localStorage (stored during login)
    const email = localStorage.getItem('user_email') || 'super_admin';

    this.programsApi.programLogin(project.id, project.uid, email).subscribe({
      next: response => {
        // Extract token from response - API returns: data.device_token
        const programToken = response.data?.device_token;

        if (!programToken) {
          console.error('No device_token in response:', response);
          this.isLoading.set(false);
          return;
        }

        console.log('Storing program data:', {
          name: project.name,
          startDate: project.startDate,
          endDate: project.endDate,
        });

        // Store the selected program with ALL data from get-programs API
        // Token comes from program-login, but all other data from get-programs
        this.programService.setProgram({
          id: project.id,
          uid: project.uid,
          programToken: programToken,
          name: project.name,
          startDate: project.startDate,
          endDate: project.endDate,
          status: project.status,
          accountManager: project.accountManager,
          managerEmail: project.managerEmail,
          managerName: project.managerName,
          programCode: project.programCode,
          applicationName: project.applicationName,
          mappingType: project.mappingType,
          salesType: project.salesType,
          createTemplate: project.createTemplate,
        });

        this.isLoading.set(false);
        // Navigate based on user role
        if (this.isSuperAdmin) {
          this.router.navigate(['/superAdmin/about-program']);
        } else if (this.isFulfillmentAdmin) {
          this.router.navigate(['/fulfillmentadmin/vendor-list']);
        } else {
          // Default fallback
          this.router.navigate(['/superAdmin/about-program']);
        }
      },
      error: err => {
        console.error('Failed to login to program:', err);
        this.isLoading.set(false);
        // Error toast is handled by programsApi service
      },
    });
  }

  /**
   * Open program configuration to manage members
   * Calls program-login API first to get program-specific token
   */
  openProgramConfig(project: Project): void {
    // Get program UID
    const programUid = project.uid;

    if (!programUid) {
      console.error('Program UID missing');
      return;
    }

    // Get stored email from localStorage
    const email = localStorage.getItem('user_email') || 'super_admin';

    if (!email) {
      console.error('Session expired. Please login again.');
      this.router.navigate(['/signin']);
      return;
    }

    this.isLoading.set(true);

    // Store current master token before switching to program token
    const currentToken = this.auth.getAuthToken();
    if (currentToken) {
      localStorage.setItem('master_token', currentToken);
    }

    // Call program-login API to get program-specific token
    this.programsApi
      .programLogin(project.id, programUid, email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          console.log('🔵 Program login response:', res);

          // Extract device_token from response
          const deviceToken = res.data?.device_token;

          if (!deviceToken) {
            console.error('No device_token in response:', res);
            this.isLoading.set(false);
            return;
          }

          // Save device_token as the new auth token
          this.auth.saveAuth(deviceToken);

          // Store program data for program members component
          const programData = {
            program_id: project.id,
            program_name: project.name,
            uid: programUid,
            manager_name: project.accountManager,
            prog_start_date: project.startDate,
            prog_end_date: project.endDate,
            device_token: deviceToken,
          };

          localStorage.setItem('selected_program', JSON.stringify(programData));
          this.isLoading.set(false);

          // Navigate to program members page
          this.router.navigate(['/program-members']);
        },
        error: err => {
          console.error('Program login failed:', err);
          // Restore master token on error
          const masterToken = localStorage.getItem('master_token');
          if (masterToken) {
            this.auth.saveAuth(masterToken);
            localStorage.removeItem('master_token');
          }
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Update program status
   * Status values: 1 = active, 2 = completed, 3 = hold
   */
  updateProgramStatus(project: Project, newStatus: 'ongoing' | 'completed' | 'on-hold'): void {
    // Don't update if already in progress
    if (this.updatingProgramId() !== null) return;

    // Don't update if status hasn't changed
    if (project.status === newStatus) return;

    // Map frontend status to API status codes
    const statusMap: Record<'ongoing' | 'completed' | 'on-hold', number> = {
      ongoing: 1,
      completed: 2,
      'on-hold': 3,
    };

    const statusCode = statusMap[newStatus];
    this.updatingProgramId.set(project.id);

    this.programsApi.updateProgramStatus(project.id, statusCode, project.uid).subscribe({
      next: () => {
        // Reload programs list to get updated data
        this.loadPrograms();
        this.updatingProgramId.set(null);
      },
      error: error => {
        console.error('Failed to update program status:', error);
        // Reload to revert the dropdown to the actual server status
        this.loadPrograms();
        this.updatingProgramId.set(null);
        // Error toast is handled by programsApi service
      },
    });
  }

  getTabLabel(tab: TabType): string {
    const labels = {
      ongoing: 'Ongoing',
      completed: 'Completed',
      'on-hold': 'On Hold',
    };
    return labels[tab];
  }

  getTabCount(tab: TabType): number {
    const counts = {
      ongoing: this.ongoingCount(),
      completed: this.completedCount(),
      'on-hold': this.onHoldCount(),
    };
    return counts[tab];
  }

  getTabIcon(tab: TabType): string {
    const icons = {
      ongoing: 'ti-control-play',
      completed: 'ti-check',
      'on-hold': 'ti-control-pause',
    };
    return icons[tab];
  }

  /**
   * Navigate to Account Manager page
   */
  navigateToAccountManager(): void {
    this.router.navigate(['/superAdmin/account-manager']);
  }

  /**
   * Logout user and redirect to signin page
   */
  logout(): void {
    this.programService.clearProgram();
    this.auth.logout();
  }
}
